import { axiosApiInstance } from "./axios";
import { baseApiPath, get, post } from "./base-api";
import {
  ISupportContextResponse,
  ISupportConversation,
  ISupportConversationsParams,
  ISupportConversationsResponse,
  ISupportMessage,
  ISupportThreadResponse,
  ISupportUnreadSummary,
  SupportConversationStatus,
} from "../data-access";

/**
 * Staff half of the support chat API. Every route sits behind the backend's
 * `staff` middleware, which answers 404 to everyone else - that 404 is what
 * keeps the inbox private, so callers must treat a rejection as "no access"
 * rather than "empty list".
 */

const supportPath = `${baseApiPath}/api/support/conversations`;

const buildQuery = (params: Record<string, string | number | undefined>): string => {
  const query = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== "") {
      query.append(key, String(value));
    }
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : "";
};

const getConversations = ({
  status = "open",
  search,
  page,
}: ISupportConversationsParams = {}): Promise<ISupportConversationsResponse> => {
  return get<ISupportConversationsResponse>(
    `${supportPath}${buildQuery({ status, search, page })}`
  );
};

/** `before` is a message id and pages back through the history. */
const getConversation = (
  id: string,
  before?: string
): Promise<ISupportThreadResponse> => {
  return get<ISupportThreadResponse>(
    `${supportPath}/${id}${buildQuery({ before })}`
  );
};

const sendMessage = (id: string, body: string): Promise<ISupportMessage> => {
  return post<{ body: string }, ISupportMessage>(
    `${supportPath}/${id}/messages`,
    { body }
  );
};

/** Zeroes the staff-side unread counter and stamps the user's messages read. */
const markRead = (id: string): Promise<void> => {
  return post<Record<string, never>, void>(`${supportPath}/${id}/read`, {});
};

const setStatus = (
  id: string,
  status: SupportConversationStatus
): Promise<ISupportConversation> => {
  return post<{ status: SupportConversationStatus }, ISupportConversation>(
    `${supportPath}/${id}/status`,
    { status }
  );
};

const getContext = (id: string): Promise<ISupportContextResponse> => {
  return get<ISupportContextResponse>(`${supportPath}/${id}/context`);
};

/**
 * Two counters for the sidebar badge, one indexed read. Sits next to the
 * conversations collection rather than under it, hence the separate path.
 */
const getUnreadSummary = (): Promise<ISupportUnreadSummary> => {
  return get<ISupportUnreadSummary>(`${baseApiPath}/api/support/unread`);
};

/**
 * Attachments are streamed from a private disk and need the auth header, so a
 * bare <img src> gets a 401. Fetch the bytes and let the caller turn them into
 * an object URL.
 */
const getAttachmentBlob = (attachmentId: string): Promise<Blob> => {
  return axiosApiInstance
    .get(`${baseApiPath}/api/chat/attachments/${attachmentId}`, {
      responseType: "blob",
    })
    .then((response) => response.data as Blob);
};

export const supportApi = {
  getConversations,
  getConversation,
  sendMessage,
  markRead,
  setStatus,
  getContext,
  getUnreadSummary,
  getAttachmentBlob,
};
